import { ArchitectChat, type ArchitectChatTenantModel, type PipelineRecommendation } from "./ArchitectChat.js";
import type { ModelConfigChange, ApplyModelConfigResult } from "../../lib/modelConfigChange.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";

export type OperationInspectorOpStatus = "draft" | "invalid" | "published" | "paused" | "validate";

export type OperationInspectorValidationIssue = {
  path: string;
  code: string;
  message: string;
  level: "error" | "warning";
};

export type OperationInspectorGraphSummary = {
  node_count: number;
  edge_count: number;
  fan_in_points: string[];
};

export type OperationInspectorWindowRow = { name: string; type: "relative" | "absolute" };

export type OperationInspectorVariableRow = { name: string; type: string };

export type OperationInspectorOperationLevelProps = {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  tagsText: string;
  setTagsText: (v: string) => void;
  status: OperationInspectorOpStatus;
  setStatus: (v: OperationInspectorOpStatus) => void;
  save: (nextStatus?: OperationInspectorOpStatus, opts?: { silent?: boolean }) => void | Promise<unknown>;
  issues: OperationInspectorValidationIssue[];
  /** ADR-0153 Phase 5 — the SAME resolved findings the shell banner and canvas
   *  badges render: step labels, never UUIDs or raw paths. */
  resolvedFindings: Array<{
    treatment: "error" | "warning" | "pending";
    stepLabel: string | null;
    nodeId: string | null;
    field: string | null;
    message: string;
    pendingNote: string | null;
  }>;
  /** Select the offending node on the canvas — the click-through the raw table never had. */
  onSelectNode?: (nodeId: string) => void;
  graphSummary: OperationInspectorGraphSummary | null;
  validationRan: boolean;
  onValidate: () => void;
  operationWindows: OperationInspectorWindowRow[];
  operationVariables: OperationInspectorVariableRow[];
  openNewWindowModal: () => void;
  openEditWindowModal: (i: number) => void;
  deleteWindowFromList: (i: number) => void | Promise<void>;
  openNewVariableModal: () => void;
  openEditVariableModal: (i: number) => void;
  deleteVariableFromList: (i: number) => void | Promise<void>;
  operationId: string;
  aiChatBlocked: boolean;
  onAiChatRateLimited: () => void;
  onApplyPipeline: (rec: PipelineRecommendation) => void;
  onApplyStage: (rec: PipelineRecommendation, stageIndex: number) => void;
  onApplyNodeChange?: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  onDelegate?: (nodeId: string, instruction: string) => void;
  nodeLabelById?: (nodeId: string) => string | null;
  resolveModelName?: (id: string) => string | null;
  tenantAiModels: ArchitectChatTenantModel[];
  accountDefaultAiModelId: string;
};

/**
 * ADR-0129 §2.3 — the operation-level inspector (shown when nothing is selected)
 * on the shared spine: Info (name/tags/status + validation issues + graph
 * summary) · Setup (windows + variables) · AI (Architect chat). No node-level
 * Runs (§10 Q3, deferred).
 */
export function OperationInspectorOperationLevel(props: OperationInspectorOperationLevelProps) {
  const {
    name,
    setName,
    description,
    setDescription,
    tagsText,
    setTagsText,
    status,
    setStatus,
    save,
    issues,
    resolvedFindings,
    onSelectNode,
    graphSummary,
    validationRan,
    onValidate,
    operationWindows,
    operationVariables,
    openNewWindowModal,
    openEditWindowModal,
    deleteWindowFromList,
    openNewVariableModal,
    openEditVariableModal,
    deleteVariableFromList,
    operationId,
    aiChatBlocked,
    onAiChatRateLimited,
    onApplyPipeline,
    onApplyStage,
    onApplyNodeChange,
    onDelegate,
    nodeLabelById,
    resolveModelName,
    tenantAiModels,
    accountDefaultAiModelId
  } = props;

  const infoContent = (
    <>
      <div className="field">
        <label className="label" htmlFor="op-name">
          Name
        </label>
        <input id="op-name" className="input" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => void save()} />
      </div>
      <div className="field">
        <label className="label" htmlFor="op-description">
          Description
        </label>
        <textarea
          id="op-description"
          className="input reportTextarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => void save()}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="op-tags">
          Tags
        </label>
        <input
          id="op-tags"
          className="input"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          onBlur={() => void save()}
          placeholder="tag1, tag2"
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="op-status">
          Status
        </label>
        <select
          id="op-status"
          className="input"
          value={status}
          onChange={(e) => {
            const next = e.target.value as OperationInspectorOpStatus;
            setStatus(next);
            void save(next);
          }}
          disabled={status === "validate"}
        >
          <option value="draft">Draft</option>
          <option value="invalid">Invalid</option>
          <option value="published">Published</option>
          <option value="paused">Paused</option>
          {status === "validate" && <option value="validate">Validate</option>}
        </select>
      </div>

      {/* ADR-0153 Phase 5 — the roll-up: always current (findings ingest on
          load and every save; no Validate-click gate), every row identified by
          step label — never a UUID or raw path — with click-to-select. The
          Validate button stays as an explicit re-check gesture. */}
      <div className="field">
        <div className="reportSectionHeader">
          <label className="label">Validation{resolvedFindings.length > 0 ? ` (${resolvedFindings.length})` : ""}</label>
          <button className="btn btnSm" type="button" onClick={onValidate}>Re-check</button>
        </div>
        {resolvedFindings.length === 0 ? <div className="muted">No issues found.</div> : null}
        {resolvedFindings.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th></th>
                <th>Step</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {resolvedFindings.map((f, idx) => (
                <tr key={idx}>
                  <td>
                    <span className={`pill ${f.treatment === "error" ? "pillBad" : f.treatment === "warning" ? "pillWarn" : "pillNeutral"}`}>
                      {f.treatment === "error" ? "Problem" : f.treatment === "warning" ? "Warning" : "In progress"}
                    </span>
                  </td>
                  <td>
                    {f.nodeId && onSelectNode ? (
                      <button type="button" className="adminListAction" onClick={() => onSelectNode(f.nodeId!)}>
                        {f.stepLabel ?? "—"}
                      </button>
                    ) : (
                      f.stepLabel ?? "—"
                    )}
                  </td>
                  <td>{f.treatment === "pending" && f.pendingNote ? f.pendingNote : f.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {graphSummary ? (
          <div className="muted" style={{ marginTop: 4 }}>
            Nodes: {graphSummary.node_count} · Edges: {graphSummary.edge_count} · Fan-in: {graphSummary.fan_in_points.length}
          </div>
        ) : null}
      </div>
    </>
  );

  const setupContent = (
    <>
      <div className="field">
        <div className="reportSectionHeader">
          <label className="label">Windows</label>
          <button className="linkBtn menuLinkAction" type="button" onClick={openNewWindowModal}>
            New
          </button>
        </div>
        {operationWindows.length === 0 ? <div className="muted">No windows defined.</div> : null}
        {operationWindows.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {operationWindows.map((windowDef, idx) => (
                <tr key={`window-row-${idx}`}>
                  <td>{windowDef.name}</td>
                  <td>{windowDef.type === "relative" ? "Relative" : "Absolute"}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button className="linkBtn menuLinkAction" type="button" onClick={() => openEditWindowModal(idx)}>
                        Edit
                      </button>
                      <button
                        className="linkBtn menuLinkAction linkBtnDanger"
                        type="button"
                        onClick={() => {
                          void deleteWindowFromList(idx);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
      <div className="field">
        <div className="reportSectionHeader">
          <label className="label">Variables</label>
          <button className="linkBtn menuLinkAction" type="button" onClick={openNewVariableModal}>
            New
          </button>
        </div>
        {operationVariables.length === 0 ? <div className="muted">No variables defined.</div> : null}
        {operationVariables.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {operationVariables.map((variable, idx) => (
                <tr key={`variable-row-${idx}`}>
                  <td>{variable.name}</td>
                  <td>{variable.type}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button className="linkBtn menuLinkAction" type="button" onClick={() => openEditVariableModal(idx)}>
                        Edit
                      </button>
                      <button
                        className="linkBtn menuLinkAction linkBtnDanger"
                        type="button"
                        onClick={() => {
                          void deleteVariableFromList(idx);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </>
  );

  const aiContent = (
    <div className="opArchitectInspectorEmbed">
      <ArchitectChat
        operationId={operationId}
        aiChatBlocked={aiChatBlocked}
        onAiChatRateLimited={onAiChatRateLimited}
        onApplyPipeline={onApplyPipeline}
        onApplyStage={onApplyStage}
        onApplyNodeChange={onApplyNodeChange}
        onDelegate={onDelegate}
        nodeLabelById={nodeLabelById}
        resolveModelName={resolveModelName}
        tenantAiModels={tenantAiModels}
        accountDefaultAiModelId={accountDefaultAiModelId}
        layout="inspector"
      />
    </div>
  );

  return (
    <InspectorTabs
      surfaceId="operationEditor"
      tabGroupId="operationLevel"
      tabs={[
        { slot: "info", content: infoContent, badge: issues.length > 0 ? issues.length : undefined },
        { slot: "setup", content: setupContent },
        { slot: "ai", fill: true, content: aiContent }
      ]}
    />
  );
}
